/**
 * FinalisationDialog Component
 * Unified end-of-life dialog replacing separate Rollover and Terminate actions.
 * Shows per-investor financial summary (capital, interest, WHT, net value) and
 * lets user split each investor's net value into Rollover Suspense (held for
 * future rollover) vs Paid Out (disbursement). Creates a pending_changes entry
 * for approval, or executes immediately for matured/maturing contracts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  Tooltip,
  Alert,
} from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import ContractActionDialogBase from './ContractActionDialogBase';
import FinalisationPayoutsPanel from './FinalisationPayoutsPanel';

// Format number with space thousand separator and period decimal
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Band heading separating "Payable to investor" from "Still outstanding" in the Financial Summary.
// The two bands answer different questions and their figures must never be read as addable.
function BandLabel({ children, sx }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        fontWeight: 700, color: 'text.disabled', mb: 1,
        '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider' },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// A summary figure. tabular-nums keeps columns of digits aligned; `hint` marks a figure that carries
// a tooltip so it is discoverable, and tabIndex on the caller makes it keyboard-reachable.
const Figure = React.forwardRef(function Figure(
  { children, bold, color, hint, sx, ...props }, ref
) {
  return (
    <Typography
      ref={ref}
      variant="body2"
      fontWeight={bold ? 'bold' : 'medium'}
      sx={{
        color, fontVariantNumeric: 'tabular-nums',
        // `hint` figures carry a tooltip. The dotted underline must hug the text (inline-block), but
        // the tile still needs the value on its OWN line under the caption — so the inline-block sits
        // inside a block wrapper rather than becoming the tile's only child. Without the wrapper the
        // value renders beside its label ("Accrued InterestSZL 124 530.90").
        ...(hint && {
          cursor: 'help',
          borderBottom: '1px dotted',
          borderColor: 'text.disabled',
          display: 'inline-block',
          '&:hover, &:focus-visible': { borderColor: 'primary.main' },
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
});

// One line inside a tooltip breakdown.
function TipRow({ k, v, bold, indent }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="caption" sx={{ pl: indent ? 1 : 0, opacity: indent ? 0.85 : 1 }}>{k}</Typography>
      <Typography variant="caption" fontWeight={bold ? 700 : 400} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {v}
      </Typography>
    </Box>
  );
}

function TipRule() {
  return <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.28)', my: 0.75 }} />;
}

// Currency input: formatted when blurred, freely editable while focused.
//
// While focused it keeps its OWN draft string rather than mirroring the numeric state. Without that,
// every keystroke round-tripped through parseFloat and any partial entry was destroyed: typing "." gave
// NaN and was dropped, so ".17" arrived as "17" — a 100x error on a money field, and the reason
// leading-decimal entry felt impossible. The draft also preserves a trailing "." ("0.") and a typed
// "0.0" while the user is still typing the cents.
//
// The parent is still told about every keystroke, so validation and the linked suspense/payout split
// stay live as you type; only the DISPLAYED text is local.
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [draft, setDraft] = useState(null);
  const focused = draft !== null;
  const showFormatted = !focused || disabled;

  const handleChange = (e) => {
    // Digits, one optional decimal point, nothing else. Spaces are stripped so a pasted
    // "1 000.50" is accepted.
    const next = e.target.value.replace(/\s/g, '');
    if (next !== '' && !/^\d*\.?\d*$/.test(next)) return;
    setDraft(next);
    onChange(next);
  };

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : draft}
      onChange={disabled ? undefined : handleChange}
      onFocus={(e) => {
        // Seed the draft from the current value and select it, so typing replaces rather than appends.
        const num = parseFloat(value);
        setDraft(isNaN(num) ? '' : String(num));
        requestAnimationFrame(() => e.target.select?.());
      }}
      onBlur={() => setDraft(null)}
      // Deliberately type="text" while focused, NOT type="number": a number input rejects the
      // intermediate "." outright in some browsers and silently discards a value it considers
      // invalid, which is the same class of problem this component exists to avoid.
      type="text"
      inputMode="decimal"
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

const FinalisationDialog = ({
  open,
  onClose,
  contract,
  contractId: explicitContractId,
  onSubmitSuccess,
  /* T3-166. 'create' (default) finalises the contract. 'view' shows what WAS recorded at
     finalisation, read-only. Mirrors MatchDialog's `mode` prop rather than a boolean, so a third
     mode can be added later without rewriting every call site. */
  mode = 'create',
  /* T3-171. View mode only: called after a Finalisation Payout is confirmed as paid, so the caller
     can reload the schedule (Settling -> Paid). */
  onPayoutConfirmed,
}) => {
  const contractId = explicitContractId || contract?.id;
  const isView = mode === 'view';

  const [finalisationDate, setFinalisationDate] = useState('');
  const [reason, setReason] = useState('');
  const [investorRows, setInvestorRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // T3-107: contract-level residual split behind the Accrued Interest figure. Returned repeated on
  // every investor row by fn_contract_get_finalisation_summary, so read it from the first row only.
  const [residual, setResidual] = useState(null);

  // Set default finalisation date when dialog opens
  // For Matured: use maturity date. For Active: use today.
  useEffect(() => {
    // In view mode the date comes from the stored record, not from today/maturity -- defaulting it
    // here would overwrite the finalisation date with a guess before fetchDetail lands.
    if (open && contract && !isView) {
      if (contract.status === 'Matured' && contract.maturity_date) {
        setFinalisationDate(format(new Date(contract.maturity_date), 'yyyy-MM-dd'));
      } else {
        setFinalisationDate(format(new Date(), 'yyyy-MM-dd'));
      }
      setReason('');
      setInvestorRows([]);
      setError(null);
    }
  }, [open, contract, isView]);

  // Fetch financial summary when date changes
  const fetchSummary = useCallback(async (date) => {
    if (!contractId || !date) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/contracts/${contractId}/finalisation-summary?finalisation_date=${date}`);
      const data = result.data || [];
      // T3-117: writeoff_amount is CONTRACT-level and repeated on every investor row, so it is
      // apportioned here by each investor's share of the interest — the residual arises on interest
      // lines, so interest is the basis that matches where it came from.
      //
      // T3-124: the rounding remainder goes to the investor with the LARGEST CAPITAL (ties broken by
      // lowest investor_id), not to the last row. Row order is an ORDER BY, not a contract — and the
      // SQL side uses exactly this rule for its own rounding dust
      // (amortizationService.js:4040-4046, "deliberately NOT 'the last investor'"). Two different
      // remainder rules in one dialog is the quiet kind of inconsistency this codebase keeps paying
      // for; the amounts differ by at most a cent but the rule is now statable in one sentence.
      const contractWriteoff = parseFloat(data[0]?.writeoff_amount) || 0;
      const totalInterest = data.reduce((s, r) => s + (parseFloat(r.accrued_interest) || 0), 0);
      const remainderIdx = data.reduce((best, r, i) => {
        const cap = parseFloat(r.original_capital) || 0;
        const bestCap = parseFloat(data[best].original_capital) || 0;
        if (cap > bestCap) return i;
        if (cap === bestCap && (r.investor_id ?? Infinity) < (data[best].investor_id ?? Infinity)) return i;
        return best;
      }, 0);
      // Two passes, deliberately: the remainder investor is not necessarily LAST in the array, so a
      // single accumulating pass would hand them a remainder computed before the rows after them had
      // contributed — over-allocating (measured: a 1,000 ceiling produced 1,066.67 when the largest
      // investor sorted first). Pass 1 prorates everyone else; pass 2 gives the remainder investor
      // whatever is left, so the total is exact for ANY row order.
      const proRated = data.map((inv, idx) => {
        if (idx === remainderIdx) return 0;
        const interest = parseFloat(inv.accrued_interest) || 0;
        return totalInterest > 0
          ? Math.round(contractWriteoff * (interest / totalInterest) * 100) / 100
          : 0;
      });
      const allocatedTotal = proRated.reduce((s, v) => s + v, 0);
      const rows = data.map((inv, idx) => {
        const woMax = idx === remainderIdx
          ? Math.round((contractWriteoff - allocatedTotal) * 100) / 100
          : proRated[idx];
        return {
          investor_id: inv.investor_id,
          investor_name: inv.investor_name,
          original_capital: parseFloat(inv.original_capital) || 0,
          accrued_interest: parseFloat(inv.accrued_interest) || 0,
          wht_rate: parseFloat(inv.wht_rate) || 0,
          wht_gross_up: inv.wht_gross_up || false,
          wht_amount: parseFloat(inv.wht_amount) || 0,
          // T3-126: the system-derived provision, kept alongside the (editable) wht_amount above.
          // The API stamps THIS onto the schedule anchor row, so an override of wht_amount yields a
          // variance row rather than silently agreeing with itself. Never edited in the UI.
          wht_computed_amount: parseFloat(inv.wht_amount) || 0,
          // T3-126: 'remit' (we withhold and remit — payout NET) | 'payout' (handed to the investor
          // for a third party to remit — payout GROSS). Defaults to today's behaviour.
          wht_treatment: 'remit',
          // T3-124: WHT owed by RATE on lines that store no provision, reported separately so it is
          // never applied silently. The operator actions it or leaves 0.
          wht_rate_fallback_amount: parseFloat(inv.wht_rate_fallback_amount) || 0,
          wht_provision_missing: inv.wht_provision_missing === true,
          net_value: parseFloat(inv.net_value) || 0,
          // Default: all to suspense. Consistent with writeoff_amount starting at 0 below — when the
          // write-off was instead pre-filled at its maximum, these two were seeded independently and
          // the dialog opened in an impossible state (1,000 written off AND the full net value
          // allocated) because no handler had run to reconcile them. Keep the two defaults in step.
          reserve_amount: parseFloat(inv.net_value) || 0,
          payout_amount: 0,
          // The most this investor may write off, and the amount actually being taken.
          //
          // STARTS AT ZERO, deliberately. It was previously pre-filled with the maximum, which meant
          // simply opening the dialog proposed forgiving money nobody had asked to forgive — and the
          // operator had to notice and undo it. A write-off is a decision; the default is not to make
          // it. writeoff_max is what they MAY write off, writeoff_amount is what they choose to.
          writeoff_max: Math.max(0, woMax),
          writeoff_amount: 0,
          // T3-174: this investor's settlement allowance (T3-169 finalisation_overpay_days), from the
          // same helper sp_contract_finalise and the API use. 0.01 = exact, the default.
          allocation_tolerance: parseFloat(inv.allocation_tolerance) || 0.01,
        };
      });
      setInvestorRows(rows);
      // Contract-level, identical on every row — take it from the first.
      const first = (result.data || [])[0];
      setResidual(first ? {
        shortPaid: parseFloat(first.interest_short_paid) || 0,
        notYetPaid: parseFloat(first.interest_not_yet_paid) || 0,
        overPaid: parseFloat(first.interest_over_paid) || 0,
        whtNotYetPaid: parseFloat(first.wht_not_yet_paid) || 0,
        accruedNotDue: parseFloat(first.interest_accrued_not_due) || 0,
        // T3-108: false when the breakdown cannot be trusted even though it still sums
        // correctly. Treat a missing field (pre-T3-108 database) as trustworthy so the
        // warning never fires spuriously on an un-migrated environment.
        reconciled: first.residual_reconciled !== false,
        // T3-117: contract-level, same shape as the residual figures above.
        writeoffEligible: parseFloat(first.writeoff_eligible_amount) || 0,
        writeoffAmount: parseFloat(first.writeoff_amount) || 0,
        writeoffWithinLimit: first.writeoff_within_limit === true,
        // T3-124: contract-level attribution quality. On a multi-investor contract whose schedule
        // rows carry no investor_id (the T3-113 unsplit shape) the per-investor Interest/WHT cannot
        // be derived and fall back to capital-share proration — the operator must be told.
        // A MISSING field means an un-migrated database, which is treated as complete so the warning
        // never fires spuriously — same defensive rule as residual_reconciled above.
        unattributedInterest: parseFloat(first.unattributed_interest) || 0,
        attributionComplete: first.investor_attribution_complete !== false,
        // T3-124: total WHT owed by rate where no provision is stored, across all investors.
        whtRateFallback: (result.data || []).reduce(
          (s, r) => s + (parseFloat(r.wht_rate_fallback_amount) || 0), 0),
        // Do the "of which" components actually sum to the interest they claim to break down?
        // Computed against the SUMMED interest because the components are contract-level while
        // accrued_interest is per-investor — on a multi-investor contract only the total compares.
        componentsReconcile: (() => {
          const parts = (parseFloat(first.interest_short_paid) || 0)
                      + (parseFloat(first.interest_not_yet_paid) || 0)
                      + (parseFloat(first.interest_accrued_not_due) || 0);
          const interest = data.reduce((s, r) => s + (parseFloat(r.accrued_interest) || 0), 0);
          return Math.abs(parts - interest) <= 0.011;
        })(),
      } : null);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load financial summary');
      setInvestorRows([]);
      setResidual(null);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  // ⚠ VIEW MODE MUST NOT CALL fetchSummary. That endpoint PROJECTS what would be finalised and
  // its function raises on anything not Active/Matured, so on a Finalised contract it fails
  // outright -- and if that guard were ever relaxed it would quietly show a figure recomputed
  // against a schedule finalisation has since closed. The stored record is loaded instead.
  useEffect(() => {
    if (open && !isView && finalisationDate) {
      fetchSummary(finalisationDate);
    }
  }, [open, isView, finalisationDate, fetchSummary]);

  // T3-166: load what was ACTUALLY recorded. Straight read of contract_finalisation -- no date,
  // no recomputation. Shapes the rows to the same field names the table below already renders, so
  // the two modes share one presentation.
  const fetchDetail = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/contracts/${contractId}/finalisation-detail`);
      const data = result.data || [];
      setInvestorRows(data.map((r) => ({
        investor_id: r.investor_id,
        investor_name: r.investor_name,
        original_capital: parseFloat(r.original_capital) || 0,
        accrued_interest: parseFloat(r.accrued_interest) || 0,
        wht_amount: parseFloat(r.wht_amount) || 0,
        wht_computed_amount: parseFloat(r.wht_amount) || 0,
        wht_rate: parseFloat(r.wht_rate) || 0,
        wht_gross_up: r.wht_gross_up === true,
        wht_treatment: r.wht_treatment || 'remit',
        net_value: parseFloat(r.net_value) || 0,
        writeoff_amount: parseFloat(r.writeoff_amount) || 0,
        writeoff_max: parseFloat(r.writeoff_amount) || 0,
        reserve_amount: parseFloat(r.reserve_amount) || 0,
        payout_amount: parseFloat(r.payout_amount) || 0,
        paid_out_amount: parseFloat(r.paid_out_amount) || 0,
        wht_rate_fallback_amount: 0,
        allocation_tolerance: 0.01,
      })));
      if (data.length && data[0].finalisation_date) {
        setFinalisationDate(format(new Date(data[0].finalisation_date), 'yyyy-MM-dd'));
      }
      // The residual tiles were never persisted (see T3-166) -- suppress rather than invent them.
      setResidual(null);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load the finalisation record');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (open && isView) {
      fetchDetail();
    }
  }, [open, isView, fetchDetail]);

  // What is actually available to allocate for one investor: their net value LESS anything written
  // off. A write-off forgives part of what the investor is owed, so it must reduce the amount being
  // split between suspense and payout — otherwise the contract would be paid out in full AND record a
  // write-off, counting the same money twice. Declared before the handlers that use it.
  // T3-126: tax paid out to the investor for a THIRD PARTY to remit. Raises this row's allocatable
  // amount, because the tax leaves with the final payment instead of being withheld.
  // Gross-up means the payer absorbs the tax, so nothing was withheld and there is nothing to hand
  // over — always 0 there, whatever the treatment says.
  // ⚠ STRICTLY 'payout'. T3-165 added a third treatment, 'settled', where the recipient carries
  // the tax but the payment is NOT grossed up — so it contributes 0 here exactly as 'remit' does,
  // and its allocatable equals the 'remit' figure. Only 'payout' raises the payment.
  // Keep in step with `whtPaidOut` in routes/contracts.js (OPEN-ITEMS #109).
  // T3-173: gross-up INCLUDED. A gross-up investor's tax is the company's own; on 'payout' the
  // company funds it into the final payment for the recipient to remit, so the payment IS raised by
  // it. The exclusion that stood here assumed gross-up had no tax to hand over -- that was the defect.
  const whtPaidOutOf = (row) =>
    row.wht_treatment === 'payout' ? (row.wht_amount || 0) : 0;

  // ⚠ A SEPARATE QUESTION FROM whtPaidOutOf, and conflating the two renders the tax wrongly.
  // whtPaidOutOf asks "was the payment RAISED by the tax" (money); this asks "does someone else
  // carry it" (presentation). They agree for 'remit' and 'payout' but diverge for 'settled', where
  // the recipient carries the tax yet the payment is unchanged — so keying the colour off
  // whtPaidOutOf alone would print a settled tax as a red deduction, which is what it is NOT.
  const whtBorneByRecipient = (row) =>
    (row.wht_treatment === 'payout' || row.wht_treatment === 'settled') && !row.wht_gross_up;

  // THE ONE ALLOCATABLE CEILING. Every handler below derives from this, and the API validates the
  // identical expression (routes/contracts.js finalise route).
  //   net_value − writeoff (forgiven, allocated to nobody) + wht paid out (leaves with the payout)
  // ⚠ Keep in step with the API. Two bases for one invariant is what produced OPEN-ITEMS #109,
  // where the API used gross net_value and every non-zero write-off 400'd.
  const allocatableOf = (row) =>
    Math.max(0, row.net_value - (row.writeoff_amount || 0) + whtPaidOutOf(row));

  // T3-174: on 'payout' the tax leaves WITH the payment for the recipient to pay to the revenue
  // authority, so Payout may never be less than it. The default all-to-suspense split used to park it
  // in Rollover Suspense, where nobody pays it. Same floor as the API and sp_contract_finalise.
  const payoutFloorOf = (row) => whtPaidOutOf(row);

  // T3-174: how far Suspense + Payout may differ from Payable Gross -- the tenant's settlement
  // allowance (finalisation_overpay_days, T3-169), resolved per investor by the API. It used to be
  // unreachable: this dialog clamped to the exact figure and the API re-checked with 0.01.
  const toleranceOf = (row) => row.allocation_tolerance || 0.01;

  // Re-split after the allocatable amount changes (treatment, tax amount or write-off), keeping the
  // operator's existing suspense/payout PROPORTION rather than resetting it -- then honouring the
  // payout floor. One definition for all three handlers below.
  const resplit = (row, priorRow) => {
    const available = allocatableOf(row);
    const prior = (priorRow.reserve_amount || 0) + (priorRow.payout_amount || 0);
    const proportional = prior > 0.005
      ? Math.round(available * ((priorRow.payout_amount || 0) / prior) * 100) / 100
      : 0;
    row.payout_amount = Math.min(available, Math.max(proportional, payoutFloorOf(row)));
    row.reserve_amount = Math.max(0, Math.round((available - row.payout_amount) * 100) / 100);
    return row;
  };

  // Handle suspense amount change for an investor
  const handleSuspenseChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const available = allocatableOf(row);
      const floor = payoutFloorOf(row);
      // Up to the allowance OVER Payable Gross, with Payout never below the tax it must carry.
      row.reserve_amount = Math.max(0, Math.min(num, Math.max(0, available - floor + toleranceOf(row))));
      row.payout_amount = Math.max(floor, available - row.reserve_amount);
      updated[index] = row;
      return updated;
    });
  };

  // Handle payout amount change for an investor
  const handlePayoutChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const available = allocatableOf(row);
      // T3-174: floored at the tax on 'payout', capped at Payable Gross + the settlement allowance --
      // typing 1,127.90 against 1,127.60 with a 0.30 allowance is now accepted, not snapped back.
      row.payout_amount = Math.max(payoutFloorOf(row), Math.min(num, available + toleranceOf(row)));
      row.reserve_amount = Math.max(0, available - row.payout_amount);
      updated[index] = row;
      return updated;
    });
  };

  // T3-126: switch this investor's WHT between 'remit' (we withhold and remit — payout is NET) and
  // 'payout' (handed to the investor as part of the final payment for a third party to remit —
  // payout is GROSS). Re-splits the allocation because the allocatable amount has changed, keeping
  // the operator's existing suspense/payout proportion rather than resetting it — the same rule
  // handleWriteoffChange uses.
  const handleWhtTreatmentChange = (index, treatment) => {
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], wht_treatment: treatment };
      updated[index] = resplit(row, updated[index]);
      return updated;
    });
  };

  // T3-126: the WHT amount is operator-editable, defaulting to the computed figure. The COMPUTED
  // figure is kept separately (wht_computed_amount) and is what the API stamps onto the schedule
  // anchor row — so an override produces a contract_payment_variance row automatically and is
  // auditable. Editing the amount changes this row's net value, hence the re-split.
  const handleWhtAmountChange = (index, value) => {
    const num = Math.max(0, parseFloat(value) || 0);
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const priorRow = updated[index];
      // net_value is (principal + interest − wht), so changing the tax moves it by the delta.
      // T3-173: NOT on a gross-up row -- that tax is the company's and was never deducted from the
      // investor, so editing it must leave the investor's Net Value untouched.
      row.wht_amount = num;
      if (!priorRow.wht_gross_up) {
        row.net_value = Math.round((priorRow.net_value + (priorRow.wht_amount || 0) - num) * 100) / 100;
      }
      updated[index] = resplit(row, priorRow);
      return updated;
    });
  };

  // T3-117: write-off for one investor. Clamped to that investor's share of the eligible residual —
  // it can be reduced or zeroed, never raised. The API re-reads the tenant settings and rejects
  // anything unauthorised regardless, so this clamp is for the operator's benefit, not the control.
  //
  // Changing the write-off RE-SPLITS the allocation, because the amount available has changed. The
  // existing suspense/payout SPLIT is preserved proportionally rather than reset to all-suspense, so
  // an operator who has already decided "60% payout" does not silently lose that decision by editing
  // the write-off afterwards.
  const handleWriteoffChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row.writeoff_amount = Math.max(0, Math.min(num, row.writeoff_max));

      // resplit uses allocatableOf, not net_value − writeoff: the latter dropped tax included in the
      // payout, so on a 'payout' row editing the write-off re-split to a total the API then rejected.
      updated[index] = resplit(row, updated[index]);
      return updated;
    });
  };

  // Quick-action: all to suspense
  const setAllSuspense = () => {
    setInvestorRows((prev) =>
      // T3-174: everything EXCEPT tax that must leave with the payment ('payout' treatment).
      prev.map((row) => ({
        ...row,
        reserve_amount: Math.max(0, allocatableOf(row) - payoutFloorOf(row)),
        payout_amount: payoutFloorOf(row),
      }))
    );
  };

  // Quick-action: all paid out
  const setAllPayout = () => {
    setInvestorRows((prev) =>
      prev.map((row) => ({ ...row, reserve_amount: 0, payout_amount: allocatableOf(row) }))
    );
  };

  // Validation
  // Suspense + payout must equal the NET OF WRITE-OFF amount, not the gross net value. Validating
  // against net_value would accept an allocation that pays out the full amount while ALSO recording a
  // write-off — forgiving money and paying it out at the same time.
  // T3-174: within the investor's settlement allowance (0.01 unless the tenant set one), and the
  // payout carries any tax included in it.
  const allocationValid = investorRows.length > 0 && investorRows.every((row) => {
    const sum = row.reserve_amount + row.payout_amount;
    return Math.abs(sum - allocatableOf(row)) <= toleranceOf(row) + 0.0001
      && row.payout_amount >= payoutFloorOf(row) - 0.01;
  });
  const isValid = !!finalisationDate && allocationValid && !!reason;

  // Totals
  const totalCapital = investorRows.reduce((s, r) => s + r.original_capital, 0);
  const totalInterest = investorRows.reduce((s, r) => s + r.accrued_interest, 0);
  const totalWht = investorRows.reduce((s, r) => s + r.wht_amount, 0);
  // T3-173: the two KINDS of tax, kept apart. Deducted tax comes out of the investor's Net Value;
  // gross-up tax is the company's own expense, ON TOP, and never reduces it. One total under a label
  // chosen by "any investor is gross-up" mislabelled a mixed contract (dev Loa-SZ-2026-0003 showed an
  // investor's deducted 7,019.40 as "Company Expense" while the company's 10,529.10 appeared nowhere).
  const totalWhtDeducted = investorRows.reduce((s, r) => s + (r.wht_gross_up ? 0 : r.wht_amount), 0);
  const totalWhtCompany = investorRows.reduce((s, r) => s + (r.wht_gross_up ? r.wht_amount : 0), 0);
  // Mode column (GU / NG) shown only when some investor carries a withholding-tax rate --
  // the same "only when it says something" rule as the Payable Gross and Write-Off columns.
  const showWhtMode = investorRows.some((r) => r.wht_rate > 0);
  const totalNetValue = investorRows.reduce((s, r) => s + r.net_value, 0);
  const totalSuspense = investorRows.reduce((s, r) => s + r.reserve_amount, 0);
  const totalPayout = investorRows.reduce((s, r) => s + r.payout_amount, 0);
  // T3-117: the Write-Off column is shown only when this contract actually has something eligible.
  // A column of zeros on every other contract would be noise; the "Write-Off" tile in the Still
  // Outstanding band above always states the position, including when there is nothing to write off.
  const totalWriteoffMax = investorRows.reduce((s, r) => s + (r.writeoff_max || 0), 0);
  const totalWriteoff = investorRows.reduce((s, r) => s + (r.writeoff_amount || 0), 0);
  const showWriteoffColumn = totalWriteoffMax > 0.005;

  // Does THIS row get the WHT treatment control? Extracted from the cell so the table can ask the
  // same question of every row. Unchanged predicate — only its reach is new.
  // T3-173: no gross-up exclusion -- a gross-up investor's tax is the company's and is settled here
  // too. Keyed on the COMPUTED figure as well, so editing the amount to 0 cannot make the control
  // (and the way back) disappear.
  const hasWhtControl = (row) =>
    row.wht_amount > 0 || row.wht_computed_amount > 0;
  // If ANY investor has the control, EVERY row reserves its height. The control is a radio plus an
  // input and is much taller than the bare "-" it falls back to, so a table mixing the two put the
  // Write-Off / Suspense / Payout inputs of different rows at different heights — the row no longer
  // read across. This is the same fix the cell below already applies WITHIN a row ("keeping
  // everything on one fixed-height line holds the layout still"), lifted to the whole table.
  const anyWhtControls = investorRows.some(hasWhtControl);

  // The tax that LEAVES with the payment rather than being withheld means that, on an "Include in
  // Payout" row, what the investor actually receives is MORE than Net Value -- and Suspense + Payout
  // are constrained by that larger figure. Without a column carrying it the totals would not add across.
  // Payable Gross is ALWAYS shown (user decision 2026-10-22): it is the figure Suspense + Payout
  // must add up to, so it belongs on every contract, not only when some tax is paid out.
  // Totals-level counterpart to whtBorneByRecipient. Needed because a contract where EVERY
  // investor is 'settled' has totalWhtPaidOut = 0 — so a colour keyed on that alone would print
  // the whole total as a red deduction when none of it was deducted from anyone.
  const anyWhtBorneByRecipient = investorRows.some(whtBorneByRecipient);
  // ⚠ Derived from allocatableOf, NOT from net_value + wht. That function is the single allocatable
  // ceiling the handlers and the API both use, and it also subtracts the write-off — so with a
  // write-off present this is net_value − writeoff + wht, which is what is genuinely allocatable.
  // A second expression for the same invariant is what produced OPEN-ITEMS #109.
  const totalGross = investorRows.reduce((s, r) => s + allocatableOf(r), 0);

  // WHT treatment drives the tile wording. Matches the labels already used in
  // AmortizationScheduleTable / ApprovalDetailModal / excelExport: gross-up means the company bears
  // the tax and nothing is deducted from the investor.
  const hasGrossUp = investorRows.some((r) => r.wht_gross_up);

  // Submit
  const handleSubmit = async ({ proof_document_ids }) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        finalisation_date: finalisationDate,
        investor_allocations: investorRows.map((row) => ({
          // T3-117: each investor's own write-off, already clamped to their share of the eligible
          // residual by handleWriteoffChange. sp_contract_finalise reads writeoff_amount PER
          // allocation, so this stores per investor with no server change. The API independently
          // re-reads the tenant settings and rejects anything unauthorised.
          writeoff_amount: row.writeoff_amount || 0,
          investor_id: row.investor_id,
          original_capital: row.original_capital,
          accrued_interest: row.accrued_interest,
          wht_amount: row.wht_amount,
          wht_rate: row.wht_rate,
          wht_gross_up: row.wht_gross_up,
          // T3-126: how this investor's tax settles, and the reason for any override of the amount.
          // The API re-derives net_value / wht_computed_amount server-side and ignores whatever the
          // client sends for those — Case B pays out MORE than the computed net, so a
          // trusted-client gross would be exactly the wrong thing to accept.
          wht_treatment: row.wht_treatment || 'remit',
          wht_variance_reason:
            Math.abs((row.wht_amount || 0) - (row.wht_computed_amount || 0)) > 0.005
              ? (reason || null)
              : null,
          net_value: row.net_value,
          reserve_amount: row.reserve_amount,
          payout_amount: row.payout_amount,
        })),
        reason,
        proof_document_ids,
      };
      const response = await apiPost(`/contracts/${contractId}/finalise`, payload);
      onClose();
      if (onSubmitSuccess) onSubmitSuccess(response.data?.status === 'executed' ? 'executed' : 'pending');
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to submit finalisation');
    } finally {
      setSubmitting(false);
    }
  };

  const currencyCode = contract?.currency_code || 'ZAR';

  return (
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      workflowCode="contract_finalise"
      readOnly={isView}
      // Noun phrase in view mode ("Details", not "Finalisation") so the title itself says this is
      // a record rather than something about to happen.
      title={isView ? 'Finalisation Details' : 'Contract Finalisation'}
      description={isView
        ? 'What was recorded when this contract was finalised, per investor. Figures are read exactly as stored — nothing is recalculated. The residual breakdown, write-off eligibility and any withholding-tax override shown at the time were not persisted and so are not displayed.'
        : `Finalise this contract by allocating each investor's net value to rollover suspense (held for future rollover) or payout (disbursement). ${contract?.status === 'Matured' ? 'This will be executed immediately.' : 'This will be submitted for approval.'}`}
      submitLabel={contract?.status === 'Matured' ? 'Finalise Contract' : 'Submit for Approval'}
      submitColor="primary"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      reason={reason}
      onReasonChange={setReason}
      // The allocation table carries seven columns of currency, and the T3-117 Write-Off column
      // pushed it past what "lg" (1200px) can hold — figures like "750 000.00" were wrapping mid-
      // number, which is unreadable in a financial table.
      maxWidth="xl"
    >
      {/* Finalisation Date */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Finalisation Date"
            type="date"
            fullWidth
            value={finalisationDate}
            onChange={(e) => setFinalisationDate(e.target.value)}
            disabled={isView}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd') }}
            // "Max 30 days from today" is a constraint on CHOOSING a date. In view mode the date
            // is a fact already recorded -- and a past finalisation will routinely sit outside
            // that window, so the original helper would read as a validation failure.
            helperText={isView ? 'As recorded at finalisation' : 'Max 30 days from today'}
          />
        </Grid>
      </Grid>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">Loading financial summary...</Typography>
        </Box>
      )}

      {/* Financial Summary */}
      {!loading && investorRows.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>Financial Summary</Typography>

            {/* T3-108. The breakdown below always adds up — "Earned since last payment date" is the
                balancing figure — so a wrong figure in any other part is absorbed into it and stays
                invisible. This warning is the only signal when that happens. Finalisation is NOT
                blocked: the interest total comes from a different calculation and is unaffected, so
                blocking would stop a legitimate payout because of a display fault. */}
            {residual && !residual.reconciled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The breakdown below could not be verified, so the figures may not be complete.
                The totals are still correct and this contract can be finalised. Please report this.
              </Alert>
            )}

            {/* T3-124. This contract's schedule rows are not split per investor, so each investor's
                Interest and WHT cannot be derived from their own rows and are estimated by capital
                share instead. The CONTRACT totals are still correct. Not blocked, for the same
                reason as the T3-108 warning above: a display fault must not stop a valid payout. */}
            {residual && !residual.attributionComplete && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The per-investor Interest and Withholding Tax below are <strong>estimated</strong>,
                split by each investor&apos;s share of capital: {currencyCode}{' '}
                {formatWithSpaces(Math.abs(residual.unattributedInterest))} of interest on this
                contract&apos;s schedule is not assigned to a specific investor. The contract totals
                are correct and this contract can be finalised, but please check the per-investor
                split before paying out, and report this.
              </Alert>
            )}

            {/* T3-124. WHT is normally the provision the schedule itself carries on each interest
                line, less whatever has already been remitted — so tax already paid is never charged
                twice. This figure is the investor's rate applied to interest that carries NO charged
                tax at this date, and it covers TWO cases (fn_contract_wht_outstanding, 026:125):
                  (a) interest on payment lines that are DUE but store no tax provision, and
                  (b) accrued interest whose payment line is NOT YET DUE — e.g. an early finalisation
                      before the first interest payment (dev c376: first payment 13 Feb 2027).
                It is informational: neither case is charged, and (b) is paid gross by business rule
                (2026-10-21: "paid gross, we don't calc WHT after the fact"). The wording used to say
                the lines "carry no tax provision" (false for case b) and told the operator to "action
                it" in the Withholding Tax column, which offers no control when the charged tax is 0. */}
            {residual && residual.whtRateFallback > 0.005 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {currencyCode} {formatWithSpaces(residual.whtRateFallback)} of withholding tax at the
                investor&apos;s rate relates to interest that is <strong>not taxed</strong> in the
                figures below: interest accrued but not yet due at this date, which is paid gross,
                and any due interest whose schedule lines carry no tax provision. It is shown for
                information only.
              </Alert>
            )}

            <BandLabel>Payable to investor</BandLabel>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Outstanding Capital</Typography>
                <Figure>{currencyCode} {formatNumber(totalCapital)}</Figure>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Accrued Interest</Typography>
                {residual ? (
                  <Box>
                  <Tooltip
                    arrow
                    placement="bottom-start"
                    title={
                      <Box sx={{ py: 0.5, minWidth: 240 }}>
                        <TipRow k="Interest still owed" v={formatWithSpaces(totalInterest)} bold />
                        <TipRule />
                        <TipRow k="Short paid" v={formatWithSpaces(residual.shortPaid)} />
                        <TipRow k="Not yet paid" v={formatWithSpaces(residual.notYetPaid)} />
                        {residual.accruedNotDue > 0.005 && (
                          <TipRow k="Earned since last payment date" v={formatWithSpaces(residual.accruedNotDue)} />
                        )}
                        {residual.whtNotYetPaid > 0.005 && (
                          <TipRow
                            k="Includes withholding tax"
                            v={formatWithSpaces(residual.whtNotYetPaid)}
                            indent
                          />
                        )}
                      </Box>
                    }
                  >
                    <Figure tabIndex={0} hint>{currencyCode} {formatNumber(totalInterest)}</Figure>
                  </Tooltip>
                  </Box>
                ) : (
                  <Figure>{currencyCode} {formatNumber(totalInterest)}</Figure>
                )}
              </Grid>
              {/* T3-173: TWO tiles, one per kind of tax, each shown only when it carries a figure.
                  Deducted tax is red and in brackets because it comes OUT of Net Value; gross-up tax
                  is the company's own expense, on top, so it is neutral and never subtracted. The
                  single tile this replaces took its label from "any investor is gross-up" and so
                  mislabelled a mixed contract's deducted tax as company expense. When neither kind
                  is owed, the plain tile still states 0.00 so the band keeps its shape. */}
              {(totalWhtDeducted > 0.005 || totalWhtCompany <= 0.005) && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="textSecondary">
                    {totalWhtCompany > 0.005 ? 'Withholding Tax (Deducted)' : 'Withholding Tax'}
                  </Typography>
                  <Figure color={totalWhtDeducted > 0 ? 'error.main' : 'text.disabled'}>
                    ({currencyCode} {formatNumber(totalWhtDeducted)})
                  </Figure>
                </Grid>
              )}
              {totalWhtCompany > 0.005 && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="textSecondary">
                    Withholding Tax (Company Expense)
                  </Typography>
                  <Box>
                    <Tooltip
                      arrow
                      placement="bottom-start"
                      title="Grossed up — the company pays this tax on top of the investor's interest; it is not deducted from Net Value. For each GU investor below, choose whether we pay it to the revenue authority directly, or hand it over with the final payment for the recipient to pay on the company's behalf."
                    >
                      <Figure tabIndex={0} hint>
                        {currencyCode} {formatNumber(totalWhtCompany)}
                      </Figure>
                    </Tooltip>
                  </Box>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Total Net Value</Typography>
                {residual ? (
                  <Box>
                  <Tooltip
                    arrow
                    placement="bottom-end"
                    title={
                      <Box sx={{ py: 0.5, minWidth: 260 }}>
                        {/* Capital is DERIVED as net - interest + WHT rather than read from the
                            Outstanding Capital tile. On a MULTI-investor contract the two are not the
                            same number: the summary function emits contract_investors.capital_amount
                            as original_capital but builds net_value from a PRORATED
                            outstanding_principal, and on dev MTN-SZ-2026-0001 those differ by 98,000.
                            A tooltip that explains a total must add up to that total, so it derives
                            the balancing figure instead of importing a second one. The discrepancy
                            itself is a DB-side issue, recorded separately — this does not hide it,
                            the two tiles still show their own values side by side. */}
                        {/* T3-173: DEDUCTED tax only -- gross-up tax was never taken out of Net
                            Value, so adding it back here would overstate capital by that tax. */}
                        <TipRow
                          k="Outstanding capital"
                          v={formatWithSpaces(totalNetValue - totalInterest + totalWhtDeducted)}
                        />
                        <TipRow k="Interest still owed" v={formatWithSpaces(totalInterest)} />
                        {/* Indented: these are what the interest above is MADE OF, not amounts to add
                            to it. That distinction is the whole point of this tooltip -- the "Still
                            outstanding" tiles below look like separate debts, and the natural reading
                            is that a short-paid line is owed ON TOP of the interest. It is not: it is
                            already inside it, which is why the components sum to the interest exactly. */}
                        {/* The "of which" lines are shown ONLY when they genuinely sum to the interest
                            above. On a few contracts they do not — the components exceed the interest
                            because cash was matched AHEAD of the finalisation date (OPEN-ITEM 55), so
                            dev c11 reads 78,102.74 of components against 66,102.74 of interest. Where
                            that holds, labelling them "of which" would be a false claim, so they are
                            simply omitted; residual_reconciled already flags the condition and the
                            tiles below still show each figure. */}
                        {residual.componentsReconcile && (
                          <>
                            {residual.shortPaid > 0.005 && (
                              <TipRow k="of which short paid" v={formatWithSpaces(residual.shortPaid)} indent />
                            )}
                            {residual.notYetPaid > 0.005 && (
                              <TipRow k="of which not yet paid" v={formatWithSpaces(residual.notYetPaid)} indent />
                            )}
                            {residual.accruedNotDue > 0.005 && (
                              <TipRow k="of which earned, not yet due" v={formatWithSpaces(residual.accruedNotDue)} indent />
                            )}
                          </>
                        )}
                        <TipRow
                          k="Withholding tax (deducted)"
                          v={`(${formatWithSpaces(totalWhtDeducted)})`}
                        />
                        <TipRule />
                        <TipRow k="Total net value" v={formatWithSpaces(totalNetValue)} bold />
                        {/* T3-173: stated BELOW the sum, outside it -- it is not part of Net Value. */}
                        {totalWhtCompany > 0.005 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.9 }}>
                            Withholding tax of {formatWithSpaces(totalWhtCompany)} on gross-up
                            investors is a company expense, not deducted from Net Value.
                          </Typography>
                        )}
                        {/* Deliberately OUTSIDE the sum. Over-payment is NOT currently deducted from
                            the payout (see the warning below), so listing it as a line would make this
                            tooltip disagree with the total it is explaining. */}
                        {residual.overPaid > 0.005 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.9 }}>
                            Separately, {formatWithSpaces(residual.overPaid)} was received above the
                            scheduled amount and is not yet credited here.
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <Figure tabIndex={0} hint bold>{currencyCode} {formatNumber(totalNetValue)}</Figure>
                  </Tooltip>
                  </Box>
                ) : (
                  <Figure bold>{currencyCode} {formatNumber(totalNetValue)}</Figure>
                )}
              </Grid>
            </Grid>

            {residual && (
              <>
                <BandLabel sx={{ mt: 2 }}>Still outstanding</BandLabel>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Interest Short Paid</Typography>
                    <Figure color={residual.shortPaid > 0.005 ? 'error.main' : 'text.disabled'}>
                      {residual.shortPaid > 0.005 ? `${currencyCode} ${formatNumber(residual.shortPaid)}` : '—'}
                    </Figure>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Interest Not Yet Paid</Typography>
                    <Figure color={residual.notYetPaid > 0.005 ? 'error.main' : 'text.disabled'}>
                      {residual.notYetPaid > 0.005 ? `${currencyCode} ${formatNumber(residual.notYetPaid)}` : '—'}
                    </Figure>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Withholding Tax Not Yet Paid</Typography>
                    {residual.whtNotYetPaid > 0.005 ? (
                      <Box>
                      <Tooltip
                        arrow
                        placement="bottom-start"
                        title={
                          <Box sx={{ py: 0.5, maxWidth: 260 }}>
                            {/* T3-173: the two kinds of tax answer this differently. Deducted tax
                                sits INSIDE the unpaid interest; gross-up tax is the company's own, ON
                                TOP of the interest, and is owed until remitted even where the
                                investor has been paid in full -- so "not an extra amount" was false
                                for it. Mixed contracts get both sentences. */}
                            <Typography variant="caption">
                              {totalWhtDeducted > 0.005 &&
                                'Deducted tax is part of the interest not yet paid, not an extra amount; it will be deducted from the investor when payment is made. '}
                              {totalWhtCompany > 0.005 &&
                                'Grossed-up (GU) tax is paid by the company on top of the interest, never deducted from the investor, and stays owed until the company pays it.'}
                              {totalWhtDeducted <= 0.005 && totalWhtCompany <= 0.005 &&
                                (hasGrossUp
                                  ? 'A company expense on top of the interest, never deducted from the investor.'
                                  : 'Part of the interest not yet paid, not an extra amount. It will be deducted from the investor when payment is made.')}
                            </Typography>
                          </Box>
                        }
                      >
                        <Figure tabIndex={0} hint color="warning.main">
                          {currencyCode} {formatNumber(residual.whtNotYetPaid)}
                        </Figure>
                      </Tooltip>
                      </Box>
                    ) : (
                      <Figure color="text.disabled">—</Figure>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Over Paid</Typography>
                    <Figure color={residual.overPaid > 0.005 ? 'warning.main' : 'text.disabled'}>
                      {residual.overPaid > 0.005 ? `${currencyCode} ${formatNumber(residual.overPaid)}` : '—'}
                    </Figure>
                  </Grid>
                  {/* T3-117. ALWAYS rendered, even when nothing is eligible. An earlier build showed
                      this only when a residual existed, which meant a contract with nothing to write
                      off gave no sign the feature existed at all — indistinguishable from it being
                      broken or unconfigured. The tile states which of the three it is. */}
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Write-Off</Typography>
                    {residual.writeoffEligible > 0.005 ? (
                      <Figure color={residual.writeoffAmount > 0.005 ? 'info.main' : 'warning.main'}>
                        {currencyCode} {formatNumber(residual.writeoffEligible)}
                      </Figure>
                    ) : (
                      <Tooltip
                        arrow
                        placement="bottom-start"
                        title="Nothing to write off: no part-paid line on this contract is short. A line that received no payment at all is never eligible."
                      >
                        <Figure tabIndex={0} hint color="text.disabled">—</Figure>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>

                {/* OVER-PAYMENT: cash received ABOVE the scheduled amount on a line.
                    ⚠ This is NOT currently deducted from the payout. Each line's credit is capped at
                    its own scheduled amount (the T3-103 clamp, which correctly stops one line
                    discharging its neighbours), but the surplus is then credited nowhere — so the
                    investor is paid the full outstanding interest despite having already handed over
                    this money. Measured on dev Pro-US-2026-0001: 43,726.03 of interest cash received,
                    25,890.41 credited, 17,835.62 unaccounted.
                    Surfaced here rather than silently corrected: crediting it changes the payout, so
                    it is a deliberate money-moving decision (OPEN-ITEM 57), not a UI fix. */}
                {residual.overPaid > 0.005 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>{currencyCode} {formatNumber(residual.overPaid)}</strong> was received above
                    the scheduled amount on one or more lines. This is <strong>not</strong> deducted
                    from the payout below — the investor has already paid it, so check whether it
                    should be credited or refunded before finalising.
                  </Alert>
                )}

                {/* T3-117. Explains ONLY the cases where a residual exists but CANNOT be written off.
                    When it can be, the editable Write-Off column in the allocation table below is the
                    control — a banner as well would state the same thing twice. */}
                {/* Only when NOTHING may be written off — i.e. the tenant has the feature switched
                    off (or the maximum is 0.00). When a ceiling allows even part of the residual,
                    the editable Write-Off column below is the control and says so with its own
                    "Max n.nn" hint; a banner as well would state it twice. */}
                {residual.writeoffEligible > 0.005 && residual.writeoffAmount <= 0.005 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>{currencyCode} {formatNumber(residual.writeoffEligible)}</strong> is
                    outstanding on part-paid lines. Write-off is not enabled for this tenant, so it
                    will be carried in full.
                  </Alert>
                )}
              </>
            )}
          </Paper>

          {/* Quick actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Per-Investor Allocation</Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={setAllSuspense} disabled={isView}>All to Suspense</Button>
              <Button onClick={setAllPayout} disabled={isView}>All to Payout</Button>
            </ButtonGroup>
          </Box>

          {/* Per-investor allocation table */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            {/* nowrap on every cell: a currency figure broken across two lines ("750" / "000.00")
                is unreadable and easy to misread as a different number. Set once on the table so it
                covers the totals row as well; tabular-nums keeps the digit columns aligned. The
                container already scrolls horizontally if the viewport is genuinely too narrow. */}
            <Table
              size="small"
              sx={{
                '& td, & th': { whiteSpace: 'nowrap' },
                '& td': { fontVariantNumeric: 'tabular-nums' },
                // ⚠ FIXED ROW HEIGHT, so a conditional caption cannot move anything. Three cells
                // render a caption below their value only sometimes — "estimated", "was <amount>"
                // and "+<amount> not applied". (The write-off cap used to be a fourth; it is now
                // inline beside its input, matching the column header.) MUI table cells are
                // vertically CENTRED, so the moment one of them appears the row grows and every
                // other cell's input re-centres against the new height: the inputs in a row with a
                // caption sit lower than those in a row without one, and the table jumps as the
                // operator edits. Reserving the taller height on every body row holds the inputs
                // still whether or not a caption is showing.
                //
                // 76px = a 40px size="small" input + 12px of dense cell padding + one ~20px caption
                // line. On a table row `height` acts as a MINIMUM, not a cap, so this cannot clip:
                // a row that legitimately needs more (the WHT cell can stack BOTH "was <amount>"
                // and "+<amount> not applied") still grows. That case remains uneven by design —
                // reserving for the worst case would leave a visible gap under every ordinary row.
                '& tbody tr': { height: 76 },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Investor</TableCell>
                  <TableCell align="right">Capital</TableCell>
                  <TableCell align="right">Interest</TableCell>
                  {/* Net Value FIRST, then the withholding-tax columns, then Payable Gross: the row
                      reads left to right as "what the investor is owed, how the tax settles, what
                      actually leaves". (User decision 2026-10-22.) */}
                  <TableCell align="right">Net Value</TableCell>
                  {/* Spelled out, matching the summary tiles above ("Withholding Tax",
                      "Withholding Tax Not Yet Paid") — never abbreviated. */}
                  <TableCell align="right">Withholding Tax</TableCell>
                  {/* GU / NG -- same labels and tooltips as the Amortization schedule's mode column. */}
                  {showWhtMode && <TableCell align="center">Mode</TableCell>}
                  {/* The settlement DECISION gets its own headed column (it used to sit unlabelled
                      inside the Withholding Tax cell). Only when some row has a decision to make. */}
                  {anyWhtControls && <TableCell align="center" sx={{ minWidth: 200 }}>Treatment</TableCell>}
                  {/* What actually leaves for this investor: net_value − writeoff + tax paid out,
                      i.e. allocatableOf -- the figure Suspense + Payout must equal. */}
                  <TableCell align="right" sx={{ minWidth: 120 }}>Payable Gross</TableCell>
                  {/* The cap is stated ONCE in this header, inline beside the label rather than on a
                      second line — it is a contract-level figure, so a per-row hint would repeat the
                      same number down the whole table on a contract with many investors. */}
                  {showWriteoffColumn && (
                    <TableCell align="right" sx={{ minWidth: 180 }}>
                      Write-Off
                      <Typography
                        component="span"
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 400, ml: 0.75 }}
                      >
                        (max {formatWithSpaces(totalWriteoffMax)})
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ minWidth: 160 }}>Rollover Suspense</TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>Payout</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {investorRows.map((row, idx) => (
                  <TableRow key={row.investor_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{row.investor_name}</Typography>
                      {row.wht_rate > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          Withholding tax: {row.wht_rate.toFixed(2)}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatWithSpaces(row.original_capital)}</TableCell>
                    <TableCell align="right">
                      {formatWithSpaces(row.accrued_interest)}
                      {/* T3-124: mark the ESTIMATED rows individually. The banner says the contract
                          has unassigned interest; this says which row to distrust — on a partially
                          attributed contract that distinction is real. */}
                      {residual && !residual.attributionComplete && (
                        <Typography variant="caption" color="warning.main" display="block">
                          estimated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{formatWithSpaces(row.net_value)}</Typography>
                    </TableCell>
                    {/* The colour follows the TREATMENT. Red says "taken off", which is true where
                        we withhold and remit -- and the opposite of the truth on a row where the tax
                        travels with the payment and INCREASES what is allocatable.
                        T3-173: gross-up tax is the company's, not deducted -- neutral, unless it is
                        being paid out, when (like any paid-out tax) it RAISES the payment. */}
                    <TableCell
                      align="right"
                      sx={{
                        color: (whtBorneByRecipient(row) || whtPaidOutOf(row) > 0.005)
                          ? 'info.main'
                          : (row.wht_gross_up ? 'text.primary' : 'error.main'),
                      }}
                    >
                      {/* T3-126: the amount is EDITABLE where there is tax to settle (T3-173:
                          gross-up included). A row with no tax owed shows its figure plainly, in a
                          height-matched box when other rows carry inputs so the row reads across. */}
                      {hasWhtControl(row) ? (
                        <CurrencyInput
                          size="small"
                          disabled={isView}
                          value={row.wht_amount}
                          onChange={(v) => handleWhtAmountChange(idx, v)}
                          sx={{ maxWidth: 120, '& input': { textAlign: 'right' } }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                            minHeight: anyWhtControls ? (theme) => theme.spacing(5) : undefined,
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            {row.wht_amount > 0 ? `(${formatWithSpaces(row.wht_amount)})` : '-'}
                          </Typography>
                        </Box>
                      )}
                      {/* The override marker sits with the other captions below, so it does not
                          resize the input row when it appears. */}
                      {Math.abs((row.wht_amount || 0) - (row.wht_computed_amount || 0)) > 0.005 && (
                        <Typography variant="caption" color="warning.main" display="block">
                          was {formatWithSpaces(row.wht_computed_amount)}
                        </Typography>
                      )}
                      {/* T3-124: tax owed at the investor's rate on lines carrying no provision.
                          Shown as a separate, clearly-excluded figure — never folded into the
                          charged amount above. */}
                      {row.wht_rate_fallback_amount > 0.005 && (
                        <Tooltip
                          arrow
                          title={`At ${row.wht_rate.toFixed(2)}%, tax of ${formatWithSpaces(row.wht_rate_fallback_amount)} relates to interest that is not taxed in these figures: interest accrued but not yet due at this date (paid gross), and any due interest whose schedule lines carry no tax provision. Shown for information only.`}
                        >
                          <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                            sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                          >
                            +{formatWithSpaces(row.wht_rate_fallback_amount)} not applied
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    {showWhtMode && (
                      <TableCell align="center">
                        {row.wht_rate > 0 ? (
                          <Tooltip
                            arrow
                            title={row.wht_gross_up
                              ? 'Grossed up — withholding tax paid by company on top of interest'
                              : "Not grossed up — withholding tax deducted from investor's interest"}
                          >
                            <Box component="span" sx={{ fontWeight: 'bold', fontSize: '0.75rem', cursor: 'help' }}>
                              {row.wht_gross_up ? 'GU' : 'NG'}
                            </Box>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    )}
                    {/* T3-165/T3-173: the settlement decision, centred in its own column. Three
                        treatments, differing only in who carries the tax and whether the payment is
                        raised by it: remit -> payout NET; payout -> payout GROSS (raised by the
                        tax); settled -> payout UNCHANGED (not offered for gross-up). */}
                    {anyWhtControls && (
                      <TableCell align="center">
                        {hasWhtControl(row) ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip
                              arrow
                              title={
                                row.wht_gross_up
                                  // T3-173: gross-up wording -- the tax is the company's own expense.
                                  ? (row.wht_treatment === 'payout'
                                    ? "Grossed up — the company still pays this tax, but hands it over with the final payment so the recipient can pay it to the revenue authority on the company's behalf. The payout increases by the tax; the investor's Net Value is unchanged."
                                    : 'Grossed up — the company pays this tax to the revenue authority from its own funds. Nothing is deducted from the investor; the payout is unchanged.')
                                : row.wht_treatment === 'payout'
                                  ? 'Included in the payout: the tax is paid to the investor as part of the final payment and a third party remits it, so the payout goes GROSS — it is RAISED by the tax. The contract must hold that extra cash.'
                                  : row.wht_treatment === 'settled'
                                    ? 'Settled by the recipient: whoever is paid out carries the tax burden, but the payment is NOT raised by it. The payout and rollover figures are unchanged and the obligation is recorded as discharged.'
                                    : 'Withheld and remitted by us, so the payout is NET.'
                              }
                            >
                              <TextField
                                select
                                size="small"
                                disabled={isView}
                                value={row.wht_treatment || 'remit'}
                                onChange={(e) => handleWhtTreatmentChange(idx, e.target.value)}
                                SelectProps={{ native: true }}
                                sx={{
                                  minWidth: 188,
                                  // ⚠ HEIGHT IS PINNED TO MATCH THE AMOUNT FIELD BESIDE IT, and both
                                  // halves of this are needed. Dropping the old `py: 0.75` (6px vs
                                  // MUI's 8.5px) closed most of the gap, but the smaller font still
                                  // left the box ~3px shorter: a size="small" outlined input is
                                  // content-sized, so 0.75rem x 1.4375 line-height gives 36.25px
                                  // against the amount field's 39.13px at 0.875rem. The smaller font
                                  // stays — the treatment labels are long and must fit — so the
                                  // height is matched explicitly instead.
                                  '& .MuiOutlinedInput-root': { height: 40 },
                                  '& select': { fontSize: '0.75rem' },
                                }}
                              >
                                {/* Wording spelled out — "WHT" is never abbreviated in the UI. */}
                                <option value="remit">
                                  Withholding tax: we remit
                                </option>
                                <option value="payout">Include in payout (gross)</option>
                                {/* T3-173: not for gross-up -- the recipient never carried the company's
                                    tax, so "settled by recipient" would discharge it with nobody
                                    paying. The API and sp_contract_finalise refuse it too. */}
                                {!row.wht_gross_up && (
                                  <option value="settled">Settled by recipient</option>
                                )}
                              </TextField>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      {/* allocatableOf, so this cell can never disagree with the ceiling the
                          Suspense and Payout inputs are clamped to. Highlighted only when it differs
                          from Net Value because tax is being paid out (whtPaidOutOf). */}
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={whtPaidOutOf(row) > 0.005 ? 'info.main' : 'textSecondary'}
                      >
                        {formatWithSpaces(allocatableOf(row))}
                      </Typography>
                    </TableCell>
                    {showWriteoffColumn && (
                      <TableCell align="right">
                        {/* Each investor is capped at their OWN share of the residual, which is not
                            the contract total shown in the header. On a multi-investor contract that
                            difference matters, so the per-row cap is surfaced ONLY where it actually
                            differs — silently reducing a typed figure would look like a bug.

                            INLINE, BESIDE the input — not helperText and not a caption beneath it.
                            Both of those add a second line, which makes this cell taller than
                            Rollover Suspense and Payout and pushes every input in the row out of
                            line. Keeping it on one line removes the height difference at source.
                            Wording matches the column header's "(max N)" exactly, lowercase and
                            parenthesised, since the two state the same kind of figure.

                            ⚠ BUT IT SITS LEFT OF THE INPUT, where the header puts its own "(max N)"
                            on the right. Deliberate: the per-row caps differ in width ("(max
                            123.62)" vs "(max 75.52)"), so trailing the input pushes each row's
                            input to a different x-position and the inputs stop lining up down the
                            column. Leading it keeps every input flush right, with only the caption
                            — which nothing needs to align with — moving. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                          {investorRows.length > 1 && row.writeoff_max < totalWriteoffMax - 0.005 && (
                            <Typography variant="caption" color="textSecondary">
                              (max {formatWithSpaces(row.writeoff_max)})
                            </Typography>
                          )}
                          <CurrencyInput
                            size="small"
                            disabled={isView}
                            value={row.writeoff_amount}
                            onChange={(v) => handleWriteoffChange(idx, v)}
                            sx={{ maxWidth: 140, '& input': { textAlign: 'right' } }}
                          />
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        disabled={isView}
                        value={row.reserve_amount}
                        onChange={(v) => handleSuspenseChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        disabled={isView}
                        value={row.payout_amount}
                        onChange={(v) => handlePayoutChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                      {/* T3-174: an allocation inside the settlement allowance but not equal to
                          Payable Gross is allowed -- and must never be silent. */}
                      {(() => {
                        const diff = Math.round((row.reserve_amount + row.payout_amount - allocatableOf(row)) * 100) / 100;
                        if (Math.abs(diff) < 0.005) return null;
                        return (
                          <Typography variant="caption" color="warning.main" display="block">
                            {diff > 0 ? '+' : '−'}{formatWithSpaces(Math.abs(diff))} {diff > 0 ? 'over' : 'under'} payable
                            {' '}(allowance {formatWithSpaces(toleranceOf(row))})
                          </Typography>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalCapital)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalInterest)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalNetValue)}</Typography></TableCell>
                  {/* ONE standard figure -- the total tax on the contract (T3-173). Each row's Mode
                      says which kind it is and the Financial Summary tiles split Deducted vs Company
                      Expense. Red brackets only when EVERY row's tax is deducted from the investor. */}
                  <TableCell
                    align="right"
                    sx={{
                      color: (totalWhtCompany > 0.005 || anyWhtBorneByRecipient)
                        ? 'text.primary' : 'error.main',
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {totalWht <= 0.005
                        ? '-'
                        : (totalWhtCompany > 0.005 || anyWhtBorneByRecipient)
                          ? formatWithSpaces(totalWht)
                          : `(${formatWithSpaces(totalWht)})`}
                    </Typography>
                  </TableCell>
                  {showWhtMode && <TableCell />}
                  {anyWhtControls && <TableCell />}
                  {/* This is the line that must tie: totalGross === totalSuspense + totalPayout. */}
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="info.main">{formatWithSpaces(totalGross)}</Typography></TableCell>
                  {showWriteoffColumn && (
                    <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="info.main">{formatWithSpaces(totalWriteoff)}</Typography></TableCell>
                  )}
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="primary">{formatWithSpaces(totalSuspense)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="warning.main">{formatWithSpaces(totalPayout)}</Typography></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary chips */}
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 3, justifyContent: 'flex-end', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary">Rollover Suspense</Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {currencyCode} {formatWithSpaces(totalSuspense)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Total Payout</Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {currencyCode} {formatWithSpaces(totalPayout)}
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {/* No investors */}
      {!loading && investorRows.length === 0 && finalisationDate && !error && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          No investor data available. Please check the finalisation date.
        </Typography>
      )}

      {/* T3-171: confirm the payout(s) as paid -- turns the settled schedule lines from Settling to Paid. */}
      {isView && (
        <FinalisationPayoutsPanel contractId={contractId} currencyCode={currencyCode} onConfirmed={onPayoutConfirmed} />
      )}

      <Divider sx={{ my: 2 }} />
    </ContractActionDialogBase>
  );
};

export default FinalisationDialog;
